import {
  type Connector,
  ProviderNotFoundError,
  createConnector,
  extractRpcUrls,
} from "@wagmi/core";
import type { EthereumProvider } from "@walletconnect/ethereum-provider";
import {
  type Address,
  type ProviderConnectInfo,
  type ProviderRpcError,
  UserRejectedRequestError,
  getAddress,
} from "viem";
import { WalletConnectParameters } from "wagmi/connectors";
import {
  HederaExtensionData,
  HederaExtensionHelper,
} from "../../../services/internal/hederaService";

type WalletConnectConnector = Connector & {
  onDisplayUri(uri: string): void;
  onSessionDelete(data: { topic: string }): void;
};

export type HederaWalletConnectParameters = WalletConnectParameters & {
  extension: HederaExtensionData;
};

hederaWalletConnect.type = "hederaWalletConnect" as const;
/**
 * Wagmi connector to interact with the Hedera EVM network via the WalletConnect protocol.
 *
 * The connector removes the need for a WalletConnect modal, and instead
 * communicates with the provided `extension` by opening the extension popup
 * for user interaction upon request.
 */
export function hederaWalletConnect(parameters: HederaWalletConnectParameters) {
  const { extension, ...restParameters } = parameters;

  type Provider = Awaited<ReturnType<(typeof EthereumProvider)["init"]>>;
  type Properties = {
    connect(parameters?: { chainId?: number; pairingTopic?: string }): Promise<{
      accounts: readonly Address[];
      chainId: number;
    }>;
    onConnect(connectInfo: ProviderConnectInfo): void;
    onDisplayUri(uri: string): void;
    onSessionDelete(data: { topic: string }): void;
  };

  let provider_: Provider | undefined;
  let providerPromise: Promise<typeof provider_>;

  let connect: WalletConnectConnector["onConnect"] | undefined;
  let displayUri: WalletConnectConnector["onDisplayUri"] | undefined;
  let sessionDelete: WalletConnectConnector["onSessionDelete"] | undefined;
  let disconnect: WalletConnectConnector["onDisconnect"] | undefined;

  return createConnector<Provider, Properties>((config) => ({
    id: `hedera-wc-${extension.id}`,
    name: extension.name,
    icon: extension.icon,
    type: hederaWalletConnect.type,
    async setup() {
      const provider = await this.getProvider().catch(() => null);
      if (!provider) return;
      if (!connect) {
        connect = this.onConnect.bind(this);
        provider.on("connect", connect);
      }
      if (!sessionDelete) {
        sessionDelete = this.onSessionDelete.bind(this);
        provider.on("session_delete", sessionDelete);
      }
    },
    async connect(params = {}) {
      try {
        const provider = await this.getProvider();
        if (!provider) throw new ProviderNotFoundError();
        if (!displayUri) {
          displayUri = this.onDisplayUri;
          provider.on("display_uri", displayUri);
        }

        if (!provider.session) {
          await provider.connect({
            ...("pairingTopic" in params
              ? { pairingTopic: params.pairingTopic }
              : {}),
          });
        }

        const accounts = (await provider.enable()).map((x) => getAddress(x));
        const currentChainId = await this.getChainId();

        if (displayUri) {
          provider.removeListener("display_uri", displayUri);
          displayUri = undefined;
        }
        if (connect) {
          provider.removeListener("connect", connect);
          connect = undefined;
        }
        if (!disconnect) {
          disconnect = this.onDisconnect.bind(this);
          provider.on("disconnect", disconnect);
        }
        if (!sessionDelete) {
          sessionDelete = this.onSessionDelete.bind(this);
          provider.on("session_delete", sessionDelete);
        }

        return { accounts, chainId: currentChainId };
      } catch (error) {
        if (
          /(user rejected|connection request reset)/i.test(
            (error as ProviderRpcError)?.message
          )
        ) {
          throw new UserRejectedRequestError(error as Error);
        }
        throw error;
      }
    },
    async disconnect() {
      const provider = await this.getProvider();
      try {
        await provider?.disconnect();
      } catch (error) {
        if (!/No matching key/i.test((error as Error).message)) throw error;
      } finally {
        if (disconnect) {
          provider?.removeListener("disconnect", disconnect);
          disconnect = undefined;
        }
        if (!connect) {
          connect = this.onConnect.bind(this);
          provider?.on("connect", connect);
        }
        if (sessionDelete) {
          provider?.removeListener("session_delete", sessionDelete);
          sessionDelete = undefined;
        }
      }
    },
    async getAccounts() {
      const provider = await this.getProvider();
      return provider.accounts.map((x) => getAddress(x));
    },
    async getProvider() {
      async function initProvider() {
        const optionalChains = config.chains.map((x) => x.id) as [number];
        if (!optionalChains.length) return;
        const { EthereumProvider } = await import(
          "@walletconnect/ethereum-provider"
        );

        const rawProvider = await EthereumProvider.init({
          ...restParameters,
          disableProviderPing: true,
          optionalChains,
          projectId: restParameters.projectId,
          rpcMap: Object.fromEntries(
            config.chains.map((chain) => {
              const [url] = extractRpcUrls({
                chain,
                transports: config.transports,
              });
              return [chain.id, url];
            })
          ),
          showQrModal: false,
          // We need to specify a custom storage prefix to avoid conflicts with Wagmi's walletConnect instance
          // https://docs.reown.com/walletkit/web/usage#core-instance-sharing
          customStoragePrefix: "squid-hedera",
        });

        const proxiedProvider = new Proxy(rawProvider, {
          get(target, prop, receiver) {
            if (prop === "request") {
              return async (args: { method: string; params?: unknown[] }) => {
                const signingMethods = [
                  "eth_sendTransaction",
                  "eth_signTransaction",
                ];

                if (signingMethods.includes(args.method)) {
                  try {
                    HederaExtensionHelper.extensionOpen(extension.id);
                  } catch {}
                }

                // forward request to original provider
                return target.request(args);
              };
            }

            // forward all other properties/methods
            return Reflect.get(target, prop, receiver);
          },
        });

        return proxiedProvider;
      }

      if (!provider_) {
        if (!providerPromise) providerPromise = initProvider();
        provider_ = await providerPromise;
        provider_?.events.setMaxListeners(Number.POSITIVE_INFINITY);
      }

      return provider_!;
    },
    async getChainId() {
      const provider = await this.getProvider();
      return provider.chainId;
    },
    async isAuthorized() {
      try {
        const accounts = await this.getAccounts();

        return accounts.length > 0;
      } catch {
        return false;
      }
    },
    onAccountsChanged(accounts) {
      if (accounts.length === 0) this.onDisconnect();
      else
        config.emitter.emit("change", {
          accounts: accounts.map((x) => getAddress(x)),
        });
    },
    onChainChanged(chain) {
      const chainId = Number(chain);
      config.emitter.emit("change", { chainId });
    },
    async onConnect(connectInfo) {
      const chainId = Number(connectInfo.chainId);
      const accounts = await this.getAccounts();
      config.emitter.emit("connect", { accounts, chainId });
    },
    async onDisconnect() {
      config.emitter.emit("disconnect");

      const provider = await this.getProvider();
      if (disconnect) {
        provider.removeListener("disconnect", disconnect);
        disconnect = undefined;
      }
      if (sessionDelete) {
        provider.removeListener("session_delete", sessionDelete);
        sessionDelete = undefined;
      }
      if (!connect) {
        connect = this.onConnect.bind(this);
        provider.on("connect", connect);
      }
    },
    onDisplayUri(uri) {
      config.emitter.emit("message", { type: "display_uri", data: uri });

      HederaExtensionHelper.extensionConnect(extension.id, uri);
    },
    onSessionDelete() {
      this.onDisconnect();
    },
  }));
}
